import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** Body text explaining what the action does and its consequences. */
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Color of the confirm button. Defaults to 'primary'; use 'error' for destructive actions. */
  confirmColor?: 'primary' | 'error' | 'warning' | 'success' | 'secondary' | 'info';
  /** True while the confirmed action is in flight — disables both buttons. */
  submitting?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /** Optional extra attributes (for example in-app help keys) for the dialog paper and the two buttons. */
  paperProps?: Record<string, string>;
  confirmProps?: Record<string, string>;
  cancelProps?: Record<string, string>;
}

/**
 * Generic in-app replacement for `window.confirm(...)`. A dismissed native confirm gives the
 * user no feedback at all — this always shows an explicit Cancel/Confirm choice and can be
 * disabled while the confirmed action is submitting, so a double-click can't fire it twice.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmColor = 'primary',
  submitting = false,
  onCancel,
  onConfirm,
  paperProps,
  confirmProps,
  cancelProps,
}) => (
  <Dialog open={open} onClose={submitting ? undefined : onCancel} PaperProps={paperProps as any}>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <DialogContentText component="div">{message}</DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button {...cancelProps} onClick={onCancel} disabled={submitting}>{cancelLabel}</Button>
      <Button {...confirmProps} onClick={onConfirm} color={confirmColor} variant="contained" disabled={submitting}>
        {confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

export default ConfirmDialog;
